<template>
  <div>
    <Hero title="Impact" :imageUrl="Image"/>
    <div class="container pb-5">
        <div class="row pt-5 pb-5">
          <div class="col-12">
            <h4 class="text-blue font-weight-semi-bold pb-3">
              Targeting shared mechanisms in immune-mediated inflammatory diseases
            </h4>
            <p>
              It’s not uncommon for someone diagnosed with one autoimmune disease to then develop
              another. That is not surprising as autoimmune conditions are caused by the immune system
              mistakenly attacking our own body instead of focussing on microorganisms that can cause
              us harm. If it attacks one organ then why not another?
            </p>
            <p>
              What is surprising is that these conditions (such as rheumatoid arthritis, type 1 diabetes,
              multiple sclerosis, coeliac disease) are treated separately by doctors, researchers and
              funders alike.
            </p>
            <p>
              “We have been working with other charities to challenge this type of silo working”, says
              Professor Yuti Chernajovsky, “and instead focussing on the similarities of autoimmune
              conditions and asking what are the underlying common causes of the conditions? and can
              we develop treatments that will work for many of these conditions?”
            </p>
            <p>
              The Chernajovsky Foundation has joined forces with Connect Immune Research – a
              partnership of medical research charities each focussing on one type of autoimmune
              condition including Versus Arthritis, the MS Society, JDRF, and the British Society for
              Immunology. In 2020, we organised a meeting with experts from all around the world to
              discuss what is known about the common underlying causes of autoimmunity and which
              directions of research could be fruitful.
            </p>
            <p>
              We were asked to write  <a target="_blank" href="./Nature_Immunology_22_pp1344-1348_2021.pdf">a paper for Nature Immunology</a> which summarised the
              findings of the meeting and explained the philosophy of the initiative to challenge the
              current silo thinking.
            </p>
            <p>
              In 2021 we offered to fund researchers for one year to explore ideas that could lead to
              improving treatments and diagnostics across conditions. Ten research groups were selected
              for funding and now that they have finished we have gained much insight and already some
              are continuing their work with funding from elsewhere.
            </p>
            <p>
              The projects took a variety of approaches to developing new treatments. One targeted a
              particular type of T cell using new chemical techniques to make a bispecific antibody,
              another targeted fibroblast by engineering T cells. Others developed new small molecules
              that could reduce inflammation or target particular cell types, and others tried to identify
              new way of measuring the effectiveness of treatment. All aimed to improve treatment for
              people with autoimmune conditions.
            </p>
            <p>
              Over 2 days in July 2023, we convened a symposium at Aston University bringing all the
              grantholders together to discuss their research results and encourage collaboration.
              Keynote speakers were Professor Luke O’Neill (Trinity College) and Professor Paul Peter Tak
              (CEO Candel Therapeutics)
            </p>
            <div class="p-4">
              <div class="mb-5 col-12">
                <img class="img-fluid" src="../assets/Aston_University2.jpg" alt="">
                <p class="pt-1 image-text">Delegates at the Symposium at Aston University July 2023</p>
              </div>
              <div class="d-flex">
              <div class="d-flex flex-column col-6">
                <img class="img-fluid" src="../assets/Aston_University1.jpg" alt="">
                <p class="pt-1 image-text">Dr Montero-Melendez presents her research</p>
              </div>
              <div class="col-6">
                <img class="img-fluid" src="../assets/Aston_University3.jpg" alt="">
                <p class="pt-1 image-text">PhD students funded by the Foundation l-r: Alistair Gamble (Glasgow), Marilena Christoforu (QMUL), Naeman Ali (Birmingham), Yuti Chernajovsky, Saffron Foster (Sheffield), Lorna Chernajovsky, Joshua Jacob (Surrey), Alasdair Hood (Surrey) and Kathryn Frost (Birmingham)</p>
              </div>
              </div>
            </div>

            <p>
            <a target="_blank" href="./results_10_grants.pdf">Results of the 10 funded pilot grants can be accessed here</a>
            </p>
            <p>
              The Chernajovsky Foundation has offered the opportunity for 2 of these research groups to
              receive £500,000 each to substantially develop their ideas towards the development of new
              treatments that could work across a number of autoimmune diseases.
            </p>
            <p>
              After careful consideration, the successful groups are:
              Prof Lucy Walker at University College London, £500,000 for her work on &#39;Targeting Tfh cells
              across multiple autoimmune diseases&#39;
            </p>
            <p>
              Dr Trinidad Melendez at Queen Mary University of London has been awarded £493,276 for
              her work on &#39;Drug development and pre-clinical evaluation of novel ACTH-based peptides&#39;
            </p>
          </div>
          <hr class="col-12">
          <div class="col-12 pt-4 pb-4">
            <p>
              In 2020, 7 PhD studentships were awarded. Sadly, Covid 19 caused delayed starting dates and disrupted the smooth progress, but most students are now approaching the end of their studies. 
              Here, one of the students, Joshua Jacob, explains his research and the impact of our funding.
            </p>
            <div class="row justify-content-center">
            <video controls class="col-9">
              <source src="../assets/Video_Joshua_Jacob.mp4#t=1.9" type="video/mp4">
            </video>
            </div>
          </div>
          <hr class="col-12">
          <div class="col-12 pt-4">
          <p>
            Kathryn Frost, one of the PhD students funded by the Chernajovsky Foundation, has published <a target="_blank" href="https://www.sciencedirect.com/science/article/pii/S2666379124002660">a paper from
            her work in Cell Reports Medicine</a> showing for the first time that PEPITEM, a naturally occurring peptide,
            holds promise as a new and early treatment for osteoporosis and other conditions of bone loss.
          </p>
          <p>
            With her colleagues at Birmingham and Oxford Universities, Kathryn has demonstrated that PEPITEM
            enhances bone mineralisation, formation and strength, and reverses bone loss in animal models of disease.</p>
          <p>
            Osteoporosis is common in older people and leads to bone fractures, which impacts greatly on their quality of
            life. Fractures of the head of the femur (hip fractures) often lead to a significant reduction in mobility causing
            people to lose their independence and, in some cases, result in loss of life.
          </p>
          <p>
            Bone is constantly formed, reformed and remodelled throughout life, with around 10% of human bone
            replaced each year through a complex interplay between two cell types – osteoblasts, which form bone, and
            osteoclasts, which break down bone.
          </p>
          <p>
            As the commonly used treatments for osteoporosis have drawbacks with effectiveness and side-effects, there
            is a clear need to develop new therapeutics to stimulate bone repair.
          </p>
          <p>
            PEPITEM is a naturally occurring short protein (peptide) produced in the body and found circulating in
            everyone at low levels.
          </p>
          <p>
            The research has shown that giving additional PEPITEM prevented bone loss and improved bone density in
            animal models of the menopause, a common trigger for osteoporosis in humans. Importantly, the research
            showed similar findings in models of inflammatory bone disease (arthritis), where PEPITEM significantly
            reduced bone damage.
          </p>
          <p>
            These findings were underscored by studies using human bone cells in the laboratory obtained from older
            patients during joint surgery. Cells from older individuals responded to PEPITEM by stimulating osteoblasts to
            produce new bone.
          </p>
          <p>
            Dr Helen McGettrick, Kathryn Frost’s supervisor, said: “While the most commonly used drugs,
            bisphosphonates, work by blocking the action of osteoclasts, PEPITEM acts by swinging the balance in favour
            of bone formation, without impacting the ability of osteoclasts to resorb regions of damaged or weak bone
            tissue via normal bone remodelling.”
          </p>
          </div>
        </div>
    </div>
  </div>
</template>

<script>
import Image from '../assets/impact.jpg';
import Hero from './Hero.vue'

export default {
  name: 'app',
  components: {
    Hero
  },
  data() {
    return{
      Image
    }
  }
}
</script>

<style>
.image-text{
  font-size: 1rem;
}
</style>